import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";

import { FilterSearch, FilterSelect } from "@/components/data-table/filter-controls";
import { useDensity } from "@/components/data-table/use-density";
import { PageBody, PageHeader, Panel } from "@/components/shell/page-frame";
import { AlertQueue, SeverityLegend } from "@/features/operations/alert-queue";
import { type AlertsSearch, alertsSearchSchema } from "@/features/operations/schemas";
import { useAlertQueue } from "@/features/operations/use-alert-queue";
import { useNow } from "@/lib/use-now";

export const Route = createFileRoute("/alerts")({
  validateSearch: alertsSearchSchema,
  component: AlertsPage
});

function AlertsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const now = useNow();
  const { density } = useDensity();
  const { alerts, acknowledge, assignToMe } = useAlertQueue();

  const update = (patch: Partial<AlertsSearch>) => {
    void navigate({ search: (previous) => ({ ...previous, ...patch }) });
  };

  const filtered = useMemo(() => {
    const keyword = search.q.toLowerCase();
    return alerts.filter((alert) => {
      if (search.severity !== "all" && alert.severity !== search.severity) return false;
      if (search.state === "open" && alert.acknowledged) return false;
      if (search.state === "acknowledged" && !alert.acknowledged) return false;
      if (!keyword) return true;
      return `${alert.title} ${alert.source} ${alert.shopDomain} ${alert.detail}`
        .toLowerCase()
        .includes(keyword);
    });
  }, [alerts, search]);

  const openCount = filtered.filter((alert) => !alert.acknowledged).length;

  return (
    <>
      <PageHeader
        kind="operations"
        title="アラート"
        description="深刻度別の対応キュー。確認・割り当ては即時反映され、取り消せる"
      />

      <PageBody density="dense">
        <Panel
          title="アラート一覧"
          count={filtered.length}
          description={`未確認 ${openCount}件`}
          actions={<SeverityLegend />}
          flush
        >
          <div className="flex flex-wrap items-end gap-3 border-b border-line px-4 py-3">
            <FilterSearch
              label="事象・発生源・ストア"
              value={search.q}
              onChange={(value) => update({ q: value })}
              placeholder="アラートを検索"
            />
            <FilterSelect
              label="深刻度"
              value={search.severity}
              onChange={(value) => update({ severity: value })}
              options={[
                { value: "all", label: "すべて" },
                { value: "critical", label: "致命" },
                { value: "warning", label: "警告" },
                { value: "info", label: "情報" }
              ]}
            />
            <FilterSelect
              label="対応状態"
              value={search.state}
              onChange={(value) => update({ state: value })}
              options={[
                { value: "all", label: "すべて" },
                { value: "open", label: "未確認" },
                { value: "acknowledged", label: "確認済み" }
              ]}
            />
          </div>

          <AlertQueue
            alerts={filtered}
            now={now}
            density={density}
            onAcknowledge={acknowledge}
            onAssign={assignToMe}
            maxHeight="calc(100vh - 340px)"
          />
        </Panel>
      </PageBody>
    </>
  );
}
